import { Alert, Modal, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import moment from 'moment'

import Loading from '../../components/loading'
import { formatAmount } from '../../lib/numbers'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import Badge from '../badge'
import Icon from '../icon'
import styles from './MyDonationsModal.module.scss'
import useApi from '../../hooks/useApi'

interface MyDonationsModalProps {
  onHide: () => void
  show: boolean
  walletId: number
}

interface Donation {
  id: number
  amount: number
  charity: {
    id: number
    name: string
  }
  donated_at: string
}

export default function MyDonationsModal(props: MyDonationsModalProps) {
  const { t } = useTranslation()
  const { getCivDonations, getCivicParticipation } = useApi()
  const { currentUser } = useCurrentUser()

  const [donations, setDonations] = useState<Donation[]>([])
  const [totalDonated, setTotalDonated] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchDonations = useCallback(async () => {
    if (!props.show) return

    setIsLoading(true)
    setError(null)

    try {
      // Get donations list
      const donationsResponse = await getCivDonations()

      // Get total donated amount
      const civicResponse = await getCivicParticipation({
        userId: currentUser.id,
        start_date: moment().subtract(12, 'months').format('M-D-YYYY'),
        end_date: moment().format('M-D-YYYY'),
      })

      if (donationsResponse.status === 200 && civicResponse.status === 200) {
        const donationsData: Donation[] = donationsResponse.data
        setDonations(donationsData)

        const backendTotal = civicResponse.data.donated_amount || 0
        setTotalDonated(backendTotal)
      } else {
        throw new Error('Failed to load donations')
      }
    } catch (err) {
      console.error('Error fetching donations:', err)
      setError('Failed to load donations')
    } finally {
      setIsLoading(false)
    }
  }, [props.walletId, props.show, currentUser.id])

  useEffect(() => {
    fetchDonations()
  }, [fetchDonations])

  const onHide = () => {
    props.onHide()
    setError(null)
  }

  return (
    <Modal show={props.show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="ms-auto p-0 badge-container">
          {t('civ_wallet.my_donations_modal.title')}
          <Badge />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className={styles.modalBodyLoading}>
          <Loading show={isLoading} variant="modal" />

          {error && (
            <Alert variant="danger" className="text-center">
              {error}
            </Alert>
          )}

          {!isLoading && !error && (
            <>
              <Row className="m-4 mx-auto p-1">
                <Col className="text-center">
                  <h5 className="mb-2">
                    {t('civ_wallet.my_donations_modal.title')}
                  </h5>
                  <h3 className="display-3">{formatAmount(totalDonated)}</h3>
                  <h6 style={{ marginTop: '-1rem' }}>
                    ({donations.length}{' '}
                    {donations.length === 1 ? 'donation' : 'donations'})
                  </h6>
                </Col>
              </Row>

              {donations.length > 0 ? (
                <>
                  <Row className="fw-bold border-bottom pb-2 px-3 mb-0 mx-0">
                    <Col xs={4}>
                      {t(
                        'civ_wallet.my_donations_modal.body.chart_column_headers.charity',
                      )}
                    </Col>
                    <Col xs={4} className="text-center">
                      {t(
                        'civ_wallet.my_donations_modal.body.chart_column_headers.amount',
                      )}
                    </Col>
                    <Col xs={4} className="text-end">
                      {t(
                        'civ_wallet.my_donations_modal.body.chart_column_headers.date',
                      )}
                    </Col>
                  </Row>
                  <div
                    className="overflow-y-auto max-height-list-modal"
                    style={{ overflowX: 'hidden' }}
                  >
                    {donations.map(donation => (
                      <Row
                        key={donation.id}
                        className="py-2 p-3 border-bottom border-light mx-0"
                      >
                        <Col xs={4} className="align-self-center">
                          <span className="text-capitalize">
                            {donation.charity?.name?.toLowerCase() ||
                              t(
                                'civ_wallet.my_donations_modal.body.error.name_not_found',
                              )}
                          </span>
                        </Col>
                        <Col xs={4} className="text-center align-self-center">
                          <span>{formatAmount(donation.amount)}</span>
                        </Col>
                        <Col xs={4} className="text-end align-self-center">
                          <span>
                            {moment(donation.donated_at).format(
                              t('config.date_format'),
                            )}
                          </span>
                        </Col>
                      </Row>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <Icon name="gift" className="text-muted mb-3" />
                  <p className="text-muted">
                    {t('civ_wallet.my_donations_modal.body.error.none')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}
